import type { AppProps } from 'next/app';
import { NextPage } from 'next';
import Head from 'next/head';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store } from '../store';
import { appWithTranslation, useTranslation } from 'next-i18next/pages';
import nextI18NextConfig from '../next-i18next.config.js';
import moment from 'moment';
// Only the languages a site can be configured with
// (assets/localization/config.json); English is built in.
// 'moment/min/locales' bundled every moment locale into _app. Callers pass the
// site code (i18n.language), so a code that is not a moment locale formats in
// English, as before; "ca" (Canada) and "my" (Malaysia) now get English
// instead of Catalan and Burmese.
import 'moment/locale/de';
import 'moment/locale/hu';
import 'moment/locale/pl';
import 'moment/locale/sk';

// Defining a locale also makes it the global one, so reset the global to
// English, as 'moment/min/locales' did when it had loaded them all.
moment.locale('en');

// we need to put every css in the global scope (nextjs thingy)
import 'react-datepicker/dist/react-datepicker.css';
import 'styles/globals.css';
import 'styles/DatePicker.css';
import 'styles/PhoneNumber.css';
import 'styles/SearchField.css';
import 'styles/Select.css';
import 'react-loading-skeleton/dist/skeleton.css';
import { FC, Suspense } from 'react';
import localFont from 'next/font/local';
import { brandConfig } from 'utils/brand';

// This is required for initializing the firebase
import { AuthContextProvider } from 'context/AuthContext';
// import { initSeon } from 'config/seon';
import DeferredTracking from 'components/DeferredTracking';
import CookieConsent from 'components/CookieConsent';

// custom types for custom layouts
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  // Render without the root Suspense boundary (see MyApp).
  withoutSuspense?: boolean;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

// SEON is switched off; see the note next to its script below.
// initSeon();

// The heading font. Loaded through next/font so it is preloaded and gets a
// metric-matched fallback: text first painted in the fallback keeps its size
// when the real font swaps in, instead of reflowing the sections below it.
const gopher = localFont({
  src: '../assets/fonts/GopherText-Bold.woff2',
  weight: '700',
  style: 'normal',
  display: 'swap',
});

// WHIT-493: the Allmyles brand's font (body 400, buttons and tabs 500,
// headings 700). Only styles/globals.css's [data-brand='allmyles'] rules use
// it, and it is not preloaded, so the Tipptour build never downloads it.
// Helvetica is the styleguide's fallback.
const aeonik = localFont({
  src: [
    { path: '../assets/fonts/Aeonik-Regular.woff2', weight: '400', style: 'normal' },
    { path: '../assets/fonts/Aeonik-Medium.woff2', weight: '500', style: 'normal' },
    { path: '../assets/fonts/Aeonik-Bold.woff2', weight: '700', style: 'normal' },
  ],
  display: 'swap',
  preload: false,
  fallback: ['Helvetica', 'Arial', 'sans-serif'],
});

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout || (page => page);
  const isProduction = process.env.NEXT_PUBLIC_ENV === 'production';
  // Built once per render of MyApp and handed to PersistGate as the same
  // element: PersistGate re-renders when redux-persist finishes restoring the
  // store, and returning an unchanged element lets React skip the whole page
  // instead of re-rendering it (store changes still reach components through
  // their selectors). The Suspense boundary makes React 18 hydrate the page
  // at low priority in small slices rather than in one long task; nothing in
  // it suspends, so its fallback never shows.
  const content = getLayout(
    <>
      <Head>
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`${brandConfig.faviconDir}/apple-touch-icon.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`${brandConfig.faviconDir}/favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`${brandConfig.faviconDir}/favicon-16x16.png`}
        />
        <link
          rel="manifest"
          href={`${brandConfig.faviconDir}/site.webmanifest`}
        />
        <link
          rel="mask-icon"
          href={`${brandConfig.faviconDir}/safari-pinned-tab.svg`}
          color="#5bbad5"
        />
        <meta
          name="msapplication-TileColor"
          content={brandConfig.tileColor}
        />
        <meta name="theme-color" content="#ffffff"></meta>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"></meta>

        {/* disable robot for now on staging environment */}
        {!isProduction && <meta name="robots" content="none" />}
      </Head>
      {/* SEON fraud checks are switched off (no free plan any more).
            Kept so they can be re-enabled: restore this script (with
            `import Script from 'next/script'`), the initSeon() import
            and call above, and add https://cdn.seondf.com (script)
            and https://*.seondf.com (connect) back to the CSP in
            next.config.js; config/seon.ts is unchanged.
        <Script src="https://cdn.seondf.com/js/v5/agent.js" /> */}
      <DeferredTracking />
      <CookieConsent />
      <style jsx global>{`
        :root {
          --font-gopher: ${gopher.style.fontFamily};
          --font-aeonik: ${aeonik.style.fontFamily};
        }
      `}</style>
      <Component {...pageProps} />
    </>,
  );
  // React streams a Suspense boundary's content into a hidden element and
  // reveals it with an inline script. A prerendered page (the 404) has no
  // CSP nonce, so that script is blocked and the page stays blank (WHIT-479).
  const page = Component.withoutSuspense ? (
    content
  ) : (
    <Suspense fallback={null}>{content}</Suspense>
  );
  return (
    <Provider store={store}>
      <AuthContextProvider>
        <PersistGate loading={null} persistor={persistor}>
          {() => page}
        </PersistGate>
      </AuthContextProvider>
    </Provider>
  );
}

export default appWithTranslation(MyApp as FC, nextI18NextConfig);
